import type { ComponentConfig } from '@measured/puck';
import { useMemo } from 'react';
import {
  ArrowUpRight,
  Briefcase,
  Building,
  Building2,
  CheckCircle2,
  Cog,
  Factory,
  HardHat,
  Home,
  Landmark,
  Sparkles,
  Tractor,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { AUDIENCES, type AudienceMeta } from '@shared/constants/audiences';
import { cn } from '../../lib/cn';

interface AudienceHubProps {
  eyebrow?: string;
  title?: string;
  description?: string;
}

const ICON_MAP: Record<AudienceMeta['iconName'], LucideIcon> = {
  home: Home,
  'building-2': Building2,
  factory: Factory,
  landmark: Landmark,
  building: Building,
  'hard-hat': HardHat,
  tractor: Tractor,
  briefcase: Briefcase,
  cog: Cog,
};

const TONE_BG: Record<AudienceMeta['tone'], string> = {
  brand: 'bg-brand-50 group-hover/card:bg-brand-100',
  lavender: 'bg-lavender-50 group-hover/card:bg-lavender-100',
  sage: 'bg-sage-100 group-hover/card:bg-sage-100',
  'lavender-700': 'bg-lavender-50 group-hover/card:bg-lavender-100',
  'brand-700': 'bg-brand-50 group-hover/card:bg-brand-100',
  accent: 'bg-accent-50 group-hover/card:bg-accent-100',
};

const TONE_TEXT: Record<AudienceMeta['tone'], string> = {
  brand: 'text-brand-700',
  lavender: 'text-lavender-600',
  sage: 'text-sage-500',
  'lavender-700': 'text-lavender-700',
  'brand-700': 'text-brand-700',
  accent: 'text-accent-700',
};

const AUDIENCE_STATS: Record<string, { metric: string; label: string; benefits: string[] }> = {
  particuliers: {
    metric: '4 aides',
    label: 'cumulables pour votre rénovation',
    benefits: [
      "Subvention Anah (MaPrimeRénov')",
      'Prime énergie (CEE)',
      'TVA à 5,5 % + prêt à taux zéro',
    ],
  },
  professionnels: {
    metric: '3 leviers',
    label: 'pour réduire vos dépenses énergie',
    benefits: [
      'Audit de factures gratuit',
      "Comparateur indépendant des fournisseurs d'énergie",
      'Primes sur vos travaux',
    ],
  },
  entreprises: {
    metric: 'Multi-sites',
    label: 'achats, conformité et primes regroupés',
    benefits: [
      'Tous vos sites pilotés ensemble',
      'Obligation −40 % 2030 sécurisée',
      'Compatible ISO 50001 (management énergie)',
    ],
  },
  tertiaire: {
    metric: '−40 % 2030',
    label: 'réduction obligatoire d’ici 2030',
    benefits: [
      'Déclaration OPERAT prise en charge',
      'Plan financé par les primes énergie',
      'Défense en cas de contrôle',
    ],
  },
  industrie: {
    metric: 'Primes industrie',
    label: 'sur équipements et utilités industrielles',
    benefits: [
      'Audit obligatoire (≥ 250 sal.)',
      'Primes sur moteurs & chaleur',
      'Aides publiques cumulables',
    ],
  },
  collectivites: {
    metric: '4 aides',
    label: 'cumulables pour votre patrimoine public',
    benefits: [
      'Fonds vert + dotations État',
      'Primes énergie (CEE)',
      'Référencé UGAP (achat public)',
    ],
  },
  coproprietes: {
    metric: 'Vote en AG',
    label: 'dès la 1re convocation visé',
    benefits: [
      'Aides collectives cumulées',
      'Projet de résolution rédigé',
      'Présentation en AG offerte',
    ],
  },
  artisans: {
    metric: '0 € avancé',
    label: 'paiement sous 30 à 60 jours',
    benefits: [
      'Mandat signé en 5 min',
      'Dossier déposé sur registre officiel',
      'Accompagnement en cas de contrôle',
    ],
  },
  agriculteurs: {
    metric: '3 aides',
    label: 'primes énergie + aides agricoles + régionales',
    benefits: ['Primes bâtiments agricoles', 'Aides régionales (PCAE)', 'Guichets nationaux'],
  },
};

interface AudienceCardProps {
  audience: AudienceMeta;
  duplicate?: boolean;
}

function AudienceCard({ audience, duplicate = false }: AudienceCardProps) {
  const Icon = ICON_MAP[audience.iconName];
  const stats = AUDIENCE_STATS[audience.id];
  if (!stats) return null;
  return (
    <a
      href={audience.href}
      tabIndex={duplicate ? -1 : undefined}
      className="group/card relative flex w-[300px] shrink-0 flex-col gap-4 rounded-xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated sm:w-[320px]"
    >
      {audience.highlight && (
        <span className="absolute -top-2 right-4 inline-flex items-center gap-1 rounded-md bg-ink px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-ivory-50">
          <Sparkles className="h-2.5 w-2.5 text-accent-400" />
          Recommandé
        </span>
      )}

      <div className="flex items-start justify-between gap-3">
        <div
          className={cn(
            'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all duration-200 ease-out group-hover/card:scale-105',
            TONE_BG[audience.tone],
            TONE_TEXT[audience.tone],
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={1.7} />
        </div>
        <div className="text-right">
          <p
            className={cn(
              'font-display text-lg font-bold tracking-tight',
              TONE_TEXT[audience.tone],
            )}
          >
            {stats.metric}
          </p>
          <p className="text-[10px] text-stone-500">{stats.label}</p>
        </div>
      </div>

      <div>
        <p className="h-card">{audience.label}</p>
        <p className="mt-1 text-xs leading-relaxed text-stone-500">{audience.tagline}</p>
      </div>

      <div className="flex flex-wrap gap-1">
        {stats.benefits.map((b) => (
          <span
            key={b}
            className="inline-flex items-center gap-1 rounded-md bg-stone-100/70 px-1.5 py-0.5 text-[10px] font-medium text-stone-700"
          >
            <CheckCircle2 className="h-2.5 w-2.5 text-stone-400" strokeWidth={2} />
            {b}
          </span>
        ))}
      </div>

      <div className="mt-auto flex items-center justify-between border-t border-stone-200/60 pt-3">
        <span
          className={cn(
            'inline-flex items-center gap-1 text-xs font-semibold transition-all duration-200 ease-out group-hover/card:gap-1.5',
            TONE_TEXT[audience.tone],
          )}
        >
          Accéder à mon espace
        </span>
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-stone-100 transition-all duration-200 ease-out group-hover/card:bg-ink group-hover/card:text-ivory-50">
          <ArrowUpRight
            className="h-3 w-3 transition-transform duration-200 ease-out group-hover/card:-translate-y-0.5 group-hover/card:translate-x-0.5"
            strokeWidth={2}
          />
        </span>
      </div>
    </a>
  );
}

interface AudienceMarqueeRowProps {
  items: AudienceMeta[];
  duration: number;
  reverse?: boolean;
}

function AudienceMarqueeRow({ items, duration, reverse = false }: AudienceMarqueeRowProps) {
  return (
    <div className="group/row mask-fade-edges flex w-full overflow-hidden">
      {[0, 1].map((dup) => (
        <ul
          key={dup}
          aria-hidden={dup === 1}
          className="flex shrink-0 items-stretch gap-4 pr-4 group-hover/row:[animation-play-state:paused] motion-reduce:!animate-none"
          style={{
            animation: `marquee ${duration}s linear infinite`,
            animationDirection: reverse ? 'reverse' : 'normal',
          }}
        >
          {items.map((a) => (
            <li key={`${dup}-${a.id}`} className="flex">
              <AudienceCard audience={a} duplicate={dup === 1} />
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}

export function AudienceHub({
  eyebrow = 'Trouvez votre parcours',
  title = 'Chacun son profil, chacun ses aides.',
  description = 'Chaque situation a ses aides et ses outils. Sélectionnez votre profil pour accéder directement à ce qui vous concerne.',
}: AudienceHubProps) {
  const items = useMemo(() => AUDIENCES, []);

  return (
    <section className="relative bg-stone-50/50 py-16 md:py-20">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-stone-300/50 to-transparent"
      />
      <Container>
        <Reveal>
          <div className="mx-auto mb-10 max-w-xl text-center md:mb-14">
            <p className="eyebrow justify-center">{eyebrow}</p>
            <h2 className="h-section mt-3">{title}</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-stone-600">{description}</p>
          </div>
        </Reveal>
      </Container>

      <Reveal delay={0.05}>
        <AudienceMarqueeRow items={items} duration={60} />
      </Reveal>

      <Container>
        <Reveal delay={0.15}>
          <p className="mt-8 text-center font-mono text-[10.5px] font-medium uppercase tracking-[0.14em] text-stone-400">
            Survolez pour mettre en pause, cliquez pour ouvrir le parcours
          </p>
        </Reveal>
      </Container>
    </section>
  );
}

export const AudienceHubConfig: ComponentConfig<AudienceHubProps> = {
  fields: {
    eyebrow: { type: 'text' },
    title: { type: 'textarea' },
    description: { type: 'textarea' },
  },
  defaultProps: {
    eyebrow: 'Trouvez votre parcours',
    title: 'Chacun son profil, chacun ses aides.',
    description:
      'Chaque situation a ses aides et ses outils. Sélectionnez votre profil pour accéder directement à ce qui vous concerne.',
  },
  render: AudienceHub,
};
